import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePassword from "@insite/client-framework/Store/Context/Handlers/UpdatePassword";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { AccountSettingsModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    password: string;
    newPassword: string;
    newPasswordError: Function;
    confirmNewPassword: string;
    error: boolean;
    showValidation: boolean;
    setShowValidation: (showValidation: boolean) => void;
    handleNewPasswordErrorMessage: Function;
    handleConfrimPasswordErrorMessage: Function;
    handleClose: Function;
    handlePopupOpenAction?: any;
}

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    accountSettingsPageLink: getPageLinkByPageType(state, "AccountSettingsPage"),
});

const mapDispatchToProps = {
    updatePassword,
};

type Props = HasHistory & OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangePasswordActionsStyles {
    headerGridContainer?: GridContainerProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    buttonGridContainer?: GridContainerProps;
    buttonGridItem?: GridItemProps;
    buttonCancelGridItem?: GridItemProps;
}

export const changePasswordActionsStyles: ChangePasswordActionsStyles = {
    saveButton: {
        css: css`
            min-width: 192px;
            height: 40px;
            margin: 0 0 0 15px;
            border-radius: 7px;
            padding: 0 10px;
            font-size: 14px;
            font-weight: 800;
            text-align: center;
        `,
    },
    cancelButton: {
        css: css`
            min-width: 91px;
            height: 40px;
            font-size: 14px;
            font-weight: 600;
            padding: 0 10px;
            text-align: center;
            border-radius: 7px;
            margin: 0;
        `,
    },
    buttonGridContainer: {
        css: css``,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: end;
        `,
    },
};

const styles = changePasswordActionsStyles;

const digitRegExp = /[0-9]/;
const lowerRegExp = /[a-z]/;
const upperRegExp = /[A-Z]/;
const specialRegExp = /\W/;
const customRegExp = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,15}$/;
const reg = /[#?!@$%^&*-]/;

const validatePassword = (password: string, settings: AccountSettingsModel) => {
    let passwordError = "";
    if (!passwordError && (!customRegExp.test(password) || reg.test(password))) {
        passwordError = translate("Your password does not meet the criteria.");
    }

    return passwordError;
};

const ADIChangePasswordActions = ({
    accountSettings,
    accountSettingsPageLink,
    password,
    newPassword,
    confirmNewPassword,
    error,
    showValidation,
    setShowValidation,
    updatePassword,
    history,
    handleClose,
    handlePopupOpenAction,
    newPasswordError,
    handleNewPasswordErrorMessage,
    handleConfrimPasswordErrorMessage,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const onSaveClick = (error?: string) => {
        if (error) {
            toasterContext.addToast({
                body: error,
                messageType: "danger",
            });

            return;
        }        
        
            toasterContext.addToast({
                body: translate("Password Updated"),
                messageType: "success",
            });

        history.push(accountSettingsPageLink!.url);
    };
   
    const updatePasswordHandler = (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        onApiResponse: (error?: string) => void,
    ) => {
        e.preventDefault();
        handleNewPasswordErrorMessage("");
        handleConfrimPasswordErrorMessage("");
        newPasswordError("");
        if (error) {
            setShowValidation(true);
        }

        if (!newPassword || !password || !confirmNewPassword) {
            setShowValidation(true);
            return;
        }

        const errorMessage = validatePassword(newPassword, accountSettings);
        if (errorMessage) {
            toasterContext.addToast({
                body: errorMessage,
                messageType: "danger",
            });
            handleNewPasswordErrorMessage(errorMessage);
            setShowValidation(true);
            return;
        }
        if (newPassword !== confirmNewPassword) {
            handleConfrimPasswordErrorMessage("Password not match");
            return;
        }

        updatePassword({
            password,
            newPassword,
            onApiResponse(error) {
                if (error) {
                    setShowValidation(true);
                    newPasswordError(error);
                } else {
                    handleClose();     
                    handlePopupOpenAction();               
                }
            },
        });
    };

    const onCancelClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        history.push(accountSettingsPageLink!.url);
    };

    const disableSaveButton = !password || !newPassword || !confirmNewPassword || (error && showValidation);

    return (
        <GridContainer {...styles.buttonGridContainer}>
            <GridItem {...styles.buttonGridItem}>
                <Button variant="secondary" {...styles.cancelButton} onClick={() => handleClose()}>
                    {translate("Cancel")}
                </Button>
                <Button
                    {...styles.saveButton}
                    onClick={event => {
                        updatePasswordHandler(event, onSaveClick);
                    }}
                >
                    {translate("Update Password")}
                </Button>
            </GridItem>
        </GridContainer>    
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIChangePasswordActions));
